import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Modal } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import MisDeudas from '../components/MisDeudas';
import { toast } from 'react-toastify';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

const Profile = () => {
  const { user, loadUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [passwordValidated, setPasswordValidated] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const sigPad = useRef({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);
    setLoading(true);

    try {
      await axios.put(`/api/users/${user.id}`, formData);
      toast.success('Perfil actualizado exitosamente');
      await loadUser(); // Recargar datos del usuario
    } catch (error) {
      const message = error.response?.data?.message || 'Error actualizando perfil';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false || passwordData.newPassword !== passwordData.confirmPassword) {
      e.stopPropagation();
      setPasswordValidated(true);
      return;
    }

    setPasswordValidated(true);
    setPasswordLoading(true);

    try {
      // Corregido: La ruta correcta para cambiar la contraseña del propio usuario podría ser diferente.
      // Asumiendo una ruta /api/auth/change-password que maneje la lógica de verificar la contraseña actual.
      await axios.put(`/api/users/${user.id}/password`, {
        newPassword: passwordData.newPassword
      });
      
      toast.success('Contraseña actualizada exitosamente');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordValidated(false);
    } catch (error) {
      const message = error.response?.data?.message || 'Error actualizando contraseña';
      toast.error(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSignatureSubmit = async () => {
    if (sigPad.current.isEmpty()) {
      toast.warn('Por favor, proporciona una firma.');
      return;
    }

    setSignatureLoading(true);
    try {
      // Obtener el canvas directamente sin usar getTrimmedCanvas que tiene bug en la versión alpha
      let canvas = null;
      
      // Método 1: Acceder al canvas interno directamente
      if (sigPad.current && sigPad.current._canvas) {
        canvas = sigPad.current._canvas;
      }
      // Método 2: Si existe getCanvas, usarlo
      else if (sigPad.current && typeof sigPad.current.getCanvas === 'function') {
        try {
          canvas = sigPad.current.getCanvas();
        } catch (e) {
          console.warn('getCanvas falló, intentando acceso directo');
        }
      }
      
      if (!canvas) {
        throw new Error('No se pudo obtener el canvas de la firma. Por favor intenta nuevamente.');
      }
      
      // Convertir a JPEG con compresión para reducir tamaño
      const firma = canvas.toDataURL('image/jpeg', 0.8);
      
      // Validar que la firma no sea demasiado grande
      if (firma.length > 5000000) { // 5MB limit
        toast.error('La firma es demasiado grande. Intenta con un trazo más simple.');
        setSignatureLoading(false);
        return;
      }

      console.log('Tamaño de la firma:', (firma.length / 1024).toFixed(2), 'KB');
      console.log('Guardando firma en el servidor...');
      
      await axios.put(`/api/users/${user.id}`, { 
        socioInfo: { ...user.socioInfo, firma } 
      });
      toast.success('Firma actualizada exitosamente');
      await loadUser();
      setShowSignatureModal(false);
    } catch (error) {
      console.error('Error al guardar firma:', error);
      const message = error.response?.data?.message || error.message || 'Error guardando la firma';
      toast.error(message);
    } finally {
      setSignatureLoading(false);
    }
  };

  const clearSignature = () => {
    sigPad.current.clear();
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    return regex.test(password) && password.length >= 6;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Container className="mt-4">
      <div className="fade-in">
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body className="text-center py-4">
                <div className="mb-3"><i className="fas fa-user-circle text-primary" style={{ fontSize: '4rem' }}></i></div>
                <h2 className="text-gradient mb-2">Mi Perfil</h2>
                <p className="text-muted">Gestiona tu información personal y configuración de cuenta</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {user?.role === 'socio' && <MisDeudas />}

        <Row>
          <Col lg={7} className="mb-4">
            <Card className="card-custom">
              <Card.Header className="card-header-custom"><h5 className="mb-0"><i className="fas fa-edit me-2"></i>Información Personal</h5></Card.Header>
              <Card.Body>
                <Form noValidate validated={validated} onSubmit={handleProfileSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="form-label-custom"><i className="fas fa-user me-2"></i>Nombre Completo</Form.Label>
                        <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} className="form-control-custom" required minLength={2} maxLength={50} />
                        <Form.Control.Feedback type="invalid">El nombre debe tener entre 2 y 50 caracteres.</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="form-label-custom"><i className="fas fa-envelope me-2"></i>Email</Form.Label>
                        <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} className="form-control-custom" required />
                        <Form.Control.Feedback type="invalid">Por favor ingresa un email válido.</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="form-label-custom"><i className="fas fa-phone me-2"></i>Teléfono</Form.Label>
                        <Form.Control type="tel" name="phone" value={formData.phone} onChange={handleChange} className="form-control-custom" placeholder="Ej: +57 300 123 4567" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="form-label-custom"><i className="fas fa-map-marker-alt me-2"></i>Dirección</Form.Label>
                        <Form.Control type="text" name="address" value={formData.address} onChange={handleChange} className="form-control-custom" placeholder="Tu dirección completa" />
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-grid gap-2">
                    <Button type="submit" className="btn-primary-custom" disabled={loading}>
                      {loading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Actualizando...</> : <><i className="fas fa-save me-2"></i>Actualizar Perfil</>}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            <Card className="card-custom mt-4">
              <Card.Header className="card-header-custom"><h5 className="mb-0"><i className="fas fa-lock me-2"></i>Cambiar Contraseña</h5></Card.Header>
              <Card.Body>
                <Form noValidate validated={passwordValidated} onSubmit={handlePasswordSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-custom"><i className="fas fa-key me-2"></i>Contraseña Actual</Form.Label>
                    <div className="position-relative">
                      <Form.Control type={showPasswords.current ? 'text' : 'password'} name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} className="form-control-custom" required />
                      <Button variant="link" className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent" style={{ zIndex: 10 }} onClick={() => togglePasswordVisibility('current')} type="button"><i className={`fas ${showPasswords.current ? 'fa-eye-slash' : 'fa-eye'}`}></i></Button>
                    </div>
                    <Form.Control.Feedback type="invalid">La contraseña actual es requerida.</Form.Control.Feedback>
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="form-label-custom"><i className="fas fa-lock me-2"></i>Nueva Contraseña</Form.Label>
                        <div className="position-relative">
                          <Form.Control type={showPasswords.new ? 'text' : 'password'} name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} className="form-control-custom" required minLength={6} isInvalid={passwordValidated && !validatePassword(passwordData.newPassword)} />
                          <Button variant="link" className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent" style={{ zIndex: 10 }} onClick={() => togglePasswordVisibility('new')} type="button"><i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'}`}></i></Button>
                        </div>
                        <Form.Control.Feedback type="invalid">La contraseña debe tener al menos 6 caracteres, una mayúscula, una minúscula y un número.</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="form-label-custom"><i className="fas fa-lock me-2"></i>Confirmar Nueva Contraseña</Form.Label>
                        <div className="position-relative">
                          <Form.Control type={showPasswords.confirm ? 'text' : 'password'} name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} className="form-control-custom" required isInvalid={passwordValidated && passwordData.newPassword !== passwordData.confirmPassword} />
                          <Button variant="link" className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent" style={{ zIndex: 10 }} onClick={() => togglePasswordVisibility('confirm')} type="button"><i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'}`}></i></Button>
                        </div>
                        <Form.Control.Feedback type="invalid">Las contraseñas no coinciden.</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-grid gap-2">
                    <Button type="submit" className="btn-warning-custom" disabled={passwordLoading}>
                      {passwordLoading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Cambiando contraseña...</> : <><i className="fas fa-key me-2"></i>Cambiar Contraseña</>}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={5}>
            <Card className="card-custom mb-4">
              <Card.Header className="card-header-custom"><h5 className="mb-0"><i className="fas fa-info-circle me-2"></i>Información de Cuenta</h5></Card.Header>
              <Card.Body>
                <div className="mb-3"><strong><i className="fas fa-shield-alt me-2 text-primary"></i>Rol:</strong><div className="mt-1"><span className={`badge badge-custom ${user?.role.includes('admin') ? 'bg-danger' : 'bg-primary'}`}><i className={`fas ${user?.role.includes('admin') ? 'fa-crown' : 'fa-user'} me-1`}></i>{user?.role}</span></div></div>
                <div className="mb-3"><strong><i className="fas fa-toggle-on me-2 text-primary"></i>Estado:</strong><div className="mt-1"><span className={`badge badge-custom ${user?.isActive ? 'bg-success' : 'bg-warning'}`}><i className={`fas ${user?.isActive ? 'fa-check-circle' : 'fa-pause-circle'} me-1`}></i>{user?.isActive ? 'Activo' : 'Inactivo'}</span></div></div>
                <div className="mb-3"><strong><i className="fas fa-calendar-plus me-2 text-primary"></i>Miembro desde:</strong><div className="mt-1 text-muted">{user?.createdAt ? formatDate(user.createdAt) : 'N/A'}</div></div>
                <div className="mb-3"><strong><i className="fas fa-clock me-2 text-primary"></i>Última actualización:</strong><div className="mt-1 text-muted">{user?.updatedAt ? formatDate(user.updatedAt) : 'N/A'}</div></div>
              </Card.Body>
            </Card>

            {user?.role === 'socio' && (
              <Card className="card-custom">
                <Card.Header className="card-header-custom"><h5 className="mb-0"><i className="fas fa-signature me-2"></i>Firma Digital</h5></Card.Header>
                <Card.Body className="text-center">
                  {user.socioInfo?.firma ? (
                    <img src={user.socioInfo.firma} alt="Firma digital" style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: '4px' }} />
                  ) : (
                    <p className="text-muted">No has guardado una firma.</p>
                  )}
                  <Button variant="primary" className="mt-3" onClick={() => setShowSignatureModal(true)}><i className="fas fa-edit me-2"></i>Actualizar Firma</Button>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </div>

      <Modal show={showSignatureModal} onHide={() => setShowSignatureModal(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title><i className="fas fa-signature me-2"></i>Guardar Firma Digital</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="text-muted text-center mb-3">Dibuja tu firma en el siguiente recuadro:</p>
          <div style={{ 
            border: '2px solid #007bff', 
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            padding: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <SignatureCanvas 
              ref={sigPad} 
              canvasProps={{ 
                style: { 
                  width: '100%', 
                  height: '250px',
                  display: 'block',
                  backgroundColor: '#ffffff',
                  cursor: 'crosshair',
                  borderRadius: '4px'
                }
              }}
              backgroundColor="#ffffff"
              penColor="#000000"
            />
          </div>
          <small className="text-muted d-block mt-2">
            💡 Consejo: Dibuja tu firma de forma natural, como lo harías en papel.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSignatureModal(false)}><i className="fas fa-times me-2"></i>Cerrar</Button>
          <Button variant="warning" onClick={clearSignature}><i className="fas fa-eraser me-2"></i>Limpiar</Button>
          <Button variant="success" onClick={handleSignatureSubmit} disabled={signatureLoading}>
            {signatureLoading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Guardando...</> : <><i className="fas fa-save me-2"></i>Guardar Firma</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Profile;
